import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Home, Mail, MapPinned, Phone, Search } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { PageHero } from "./PageHero";

const quickLinks = [
  {
    title: "SEO services",
    body: "Review SEO support, audits, technical work, and ongoing organic search help.",
    to: "/seo",
    icon: Search,
  },
  {
    title: "Services overview",
    body: "See the main SEO, web design, hosting, and website management options.",
    to: "/services",
    icon: Home,
  },
  {
    title: "Areas covered",
    body: "Find local SEO and website support across Barnsley, Yorkshire, and beyond.",
    to: "/areas",
    icon: MapPinned,
  },
];

export function NotFoundPage() {
  return (
    <>
      <PageHero title="Page not found" compact />
      <section className="not-found-page inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="not-found-grid">
          <div className="not-found-intro">
            <p className="demo-section-label">404 error</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              This page has moved, changed, no longer exists, or cannot be accessed.
            </h2>
            <p className="site-copy mt-6 text-muted-foreground">
              If you followed an old link or reached a restricted area, the most useful pages are
              below. If you were looking for something specific, send a quick message and I will
              point you in the right direction.
            </p>
            <div className="not-found-actions">
              <Link to="/" className="demo-link-arrow-button">
                Home
                <span className="demo-arrow-circle">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
              <Link to="/contact" className="demo-link-arrow-button">
                Contact
                <span className="demo-arrow-circle">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          </div>

          <aside className="demo-card demo-card-accent demo-card-accent-fade not-found-help">
            <p className="demo-section-label">Need a hand?</p>
            <h3 className="font-display text-2xl">Ask about the missing page</h3>
            <p className="text-sm text-muted-foreground">
              Tell me what you were trying to find or access and I will help you get there.
            </p>
            <ul className="not-found-contact-list">
              <li>
                <Phone className="h-4 w-4" />
                <a href="tel:+441226697325">01226 697 325</a>
              </li>
              <li>
                <Mail className="h-4 w-4" />
                <a href="mailto:info@phil-carr.co.uk">info@phil-carr.co.uk</a>
              </li>
            </ul>
          </aside>
        </div>

        <ul className="not-found-link-grid">
          {quickLinks.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <Link to={item.to} className="demo-card demo-card-hover not-found-link-card group">
                  <span className="not-found-link-icon">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="demo-card-title font-display text-xl">{item.title}</span>
                  <span className="demo-card-text text-sm text-muted-foreground">{item.body}</span>
                  <span className="demo-link-arrow-button demo-card-cta mt-auto">
                    Open page
                    <span className="demo-arrow-circle">
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="inner-contact-section bg-background/95">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5">
              <p className="demo-section-label">Get in touch</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Still not found? Send me a <em>message</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                Use the form and include the page or topic you expected to see. I can help with SEO,
                web design, hosting, website management, access issues, or simply finding the right
                page.
              </p>
            </div>
            <div className="col-lg-7">
              <ContactForm pageContext="404 page" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
